/**
 * The eight platform personas and their default post-auth landing view.
 *
 * The persona strings are a WIRE CONTRACT (JWT `persona` claim). The canonical
 * definition lives in `packages/shared/src/personas.ts`; it is re-declared here
 * rather than imported so the browser bundle and the signup smoke test do not
 * depend on a built Node package. Keep the two lists identical.
 */
import { APP_VIEWS, ROUTES } from './constants';

export const PERSONAS = [
  'platform_admin',
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
  'primary_stat_operator',
  'general_stat_operator',
  'fanatic',
] as const;

export type Persona = (typeof PERSONAS)[number];

export function isPersona(value: unknown): value is Persona {
  return typeof value === 'string' && (PERSONAS as readonly string[]).includes(value);
}

/**
 * MFA is opt-in per account (off by default for every persona; the server's
 * `mfa_required` on the token response says whether THIS account must verify).
 * These admin / coaching-staff personas are the ones settings pages nudge
 * towards turning it on — nothing here forces anything.
 */
export const MFA_RECOMMENDED_PERSONAS: ReadonlySet<Persona> = new Set<Persona>([
  'platform_admin',
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
]);

/** The only persona whose tenant is not fixed by the token. */
export const MULTI_TENANT_PERSONA: Persona = 'fanatic';

/**
 * Who may star / unstar a play (team-wide favorites): coaching staff, both
 * stat operators and the Team Admin — mirrors `PLAY_FAVORITE_PERSONAS` in
 * `services/plays/src/constants.ts`. Everyone else sees stars read-only.
 */
export const PLAY_FAVORITE_PERSONAS: ReadonlySet<Persona> = new Set<Persona>([
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
  'primary_stat_operator',
  'general_stat_operator',
]);

export function canFavoritePlays(persona: Persona | null): boolean {
  return persona !== null && PLAY_FAVORITE_PERSONAS.has(persona);
}

export const PERSONA_LABELS: Record<Persona, string> = {
  platform_admin: 'Platform Admin',
  team_admin: 'Team Admin',
  offensive_coordinator: 'Offensive Coordinator',
  defensive_coordinator: 'Defensive Coordinator',
  coach: 'Coach',
  primary_stat_operator: 'Primary Stat Operator',
  general_stat_operator: 'General Stat Operator',
  fanatic: 'Fanatic',
};

/** Coach-facing personas: the sideline coaching views are built for them. */
export const COACH_FACING_PERSONAS: ReadonlySet<Persona> = new Set<Persona>([
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
]);

/**
 * Default view + query for each persona (FR-RBAC-01).
 *
 * `unit` selects the Coaches Highlight side: the Offensive Coordinator lands on
 * Offense, the Defensive Coordinator on Defense, and a Coach lands on Game
 * Summary instead.
 */
interface PersonaDestination {
  /** View slug under `/app/{tenant_slug}` — empty string means the portal home. */
  view: string;
  search?: string;
  label: string;
}

const PERSONA_DESTINATIONS: Record<Persona, PersonaDestination> = {
  platform_admin: { view: '', label: 'Platform Admin portal' },
  team_admin: { view: APP_VIEWS.home, label: 'Team Admin dashboard' },
  offensive_coordinator: {
    view: APP_VIEWS.coachesHighlight,
    search: '?unit=offense',
    label: 'Coaches Highlight — Offense',
  },
  defensive_coordinator: {
    view: APP_VIEWS.coachesHighlight,
    search: '?unit=defense',
    label: 'Coaches Highlight — Defense',
  },
  coach: { view: APP_VIEWS.gameSummary, label: 'Game Summary' },
  primary_stat_operator: { view: APP_VIEWS.playEntry, label: 'Play Entry' },
  general_stat_operator: { view: APP_VIEWS.playEntry, label: 'Play Entry' },
  fanatic: { view: '', label: 'Fanatic portal' },
};

/** Human-readable name of the persona's default landing view. */
export function defaultViewLabel(persona: Persona): string {
  return PERSONA_DESTINATIONS[persona].label;
}

/**
 * Resolve the post-auth destination path for a persona.
 *
 * - Platform Admin → `/admin` (its own portal entry point)
 * - Fanatic → `/app/fanatic-portal` (cross-tenant, team switcher)
 * - everyone else → `/app/{tenant_slug}[/{view}]`
 */
export function resolvePersonaRoute(persona: Persona, tenantSlug: string | null): string {
  if (persona === 'platform_admin') return ROUTES.admin;
  if (persona === MULTI_TENANT_PERSONA) return ROUTES.fanaticPortal;

  const destination = PERSONA_DESTINATIONS[persona];
  // Without a slug we cannot build a tenant-scoped URL; the router then asks
  // the user to re-enter their team code rather than guessing.
  if (!tenantSlug) return ROUTES.login;
  const base = `/app/${tenantSlug}`;
  const path = destination.view === '' ? base : `${base}/${destination.view}`;
  return `${path}${destination.search ?? ''}`;
}
